import { Channel, Contact, Property } from '../../src/app/shared/models/entity';
import {
  auditFields,
  createMany,
  cycle,
  FixtureBuilder,
  FixtureFactory,
  fixtureId,
} from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ ⚠ ASSUMED MODEL SHAPE
// -----------------------------------------------------------------------------------------------------
// The Channel model was not part of the models you shared, so the fields below are a guess based on
// how `Property.channels` is used. Once you paste `channel.model.ts`, only the object literal inside
// `createChannel` and the builder methods need to change — the seed, the store and the services all
// go through the factory and stay untouched.
// -----------------------------------------------------------------------------------------------------

// -----------------------------------------------------------------------------------------------------
// @ SAMPLE POOLS
// -----------------------------------------------------------------------------------------------------

const CHANNEL_NAMES: readonly string[] = [
  'Allgemein',
  'Eigentümerversammlung 2026',
  'Instandhaltung',
  'Verwaltungsbeirat',
];

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a Channel with realistic defaults. Every field can be overridden.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the sample rotation.
 * @returns {Channel} A fresh Channel instance.
 */
export const createChannel: FixtureFactory<Channel> = (
  overrides: Partial<Channel> = {},
  index: number = 0,
): Channel => {
  return new Channel({
    id: fixtureId('channel', index),
    ...auditFields(index),
    name: cycle(CHANNEL_NAMES, index),
    propertyId: '',
    propertyName: '',
    isDefault: index === 0,
    memberCount: 0,
    lastMessageAt: auditFields(index).modifiedAt,
    members: [],
    ...overrides,
  } as Partial<Channel>);
};

/**
 * Builds a list of Channels.
 *
 * @param count - How many channels to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {Channel[]} The built channels.
 */
export function createChannels(
  count: number,
  overrides?: Partial<Channel> | ((index: number) => Partial<Channel>),
): Channel[] {
  return createMany(createChannel, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_CHANNEL: Channel = createChannel();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** The channel every owner of a property is a member of. */
export const CHANNEL_GENERAL: Channel = createChannel({ isDefault: true } as Partial<Channel>, 0);

/** A restricted channel — only the Verwaltungsbeirat. */
export const CHANNEL_ADVISORY_BOARD: Channel = createChannel(
  { isDefault: false } as Partial<Channel>,
  3,
);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class ChannelBuilder extends FixtureBuilder<Channel> {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(index: number = 0) {
    super(createChannel({}, index));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Attaches the channel to a property — wires the id and the denormalized name.
   *
   * @param property - The property the channel belongs to.
   * @returns {this} The builder, for chaining.
   */
  inProperty(property: Property): this {
    return this.with({ propertyId: property.id, propertyName: property.name } as Partial<Channel>);
  }

  /**
   * Attaches the members and keeps `memberCount` in sync.
   *
   * @param members - The contacts that are members of the channel.
   * @returns {this} The builder, for chaining.
   */
  withMembers(members: Contact[]): this {
    return this.with({ members, memberCount: members.length } as Partial<Channel>);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ OVERRIDE METHODS
  // -----------------------------------------------------------------------------------------------------

  override build(): Channel {
    return new Channel(this.draft);
  }
}
